/**
 * FocusRecoveredInvigilatorModalController
 * 
 * This class manages the behavior of a modal dialog that handles the recovery of focus
 * by an invigilator. It interacts with various AngularJS services to authenticate the
 * invigilator, log events, and manage the modal's state.
 * 
 * @param {Object} $scope - The AngularJS scope object.
 * @param {Object} $uibModalInstance - The instance of the modal dialog.
 * @param {Object} $bridges - Service for interacting with bridges.
 * @param {Object} $signals - Service for handling signals.
 * @param {Object} $translate - Service for translation.
 * @param {Object} $timeout - AngularJS $timeout service.
 * @param {Object} TaskCountdownService - Service for managing task countdowns.
 * @param {Object} $interval - AngularJS $interval service.
 * @param {Object} TaskInfoService - Service for retrieving task information.
 * @param {Object} $filter - AngularJS filter service.
 */
class FocusRecoveredInvigilatorModalController {
    constructor($scope, $uibModalInstance, $bridges, $signals, $translate, $timeout, TaskCountdownService, $interval, TaskInfoService, $filter) {
        this.$scope = $scope;
        this.$uibModalInstance = $uibModalInstance;
        this.$bridges = $bridges;
        this.$signals = $signals;
        this.$translate = $translate;
        this.$timeout = $timeout;
        this.TaskCountdownService = TaskCountdownService;
        this.$interval = $interval;
        this.TaskInfoService = TaskInfoService;
        this.$filter = $filter;

        this.taskCountdown = TaskCountdownService.getInstance();
        this.startFocusLostTime = new Date().getTime();
        this.$logger = $bridges.logger();
        this.$auth = $bridges.authentication();
        this.$eventLog = $bridges.eventLog();

        this.$scope.explanationMinChar = 0;
        this.$scope.invigilatorUnlockCode = null;
        this.$scope.eventExplanation = "";
        this.$scope.disabledResumeBtn = false;
        this.$scope.invigilatorUnlockCodeInputDisabled = false;

        this.$scope.onUnlockBtn = this.onUnlockBtn.bind(this);
        this.$scope.closeUnlockErrorMessage = this.closeUnlockErrorMessage.bind(this);
        this.$scope.onKeyupPassword = this.onKeyupPassword.bind(this);
        this.$scope.onChangeExplanation = this.onChangeExplanation.bind(this);
        this.$scope.onClose = this.onClose.bind(this);

        this.onInvigilatorFailed = this.onInvigilatorFailed.bind(this);
        this.onInvigilatorSuccess = this.onInvigilatorSuccess.bind(this);

        this.$signals.remove("tryInvigilatorAuthenticationFailed");
        this.$signals.on("tryInvigilatorAuthenticationFailed", this.onInvigilatorFailed);

        this.$signals.remove("tryInvigilatorAuthenticationSuccess");
        this.$signals.on("tryInvigilatorAuthenticationSuccess", this.onInvigilatorSuccess);

        this.$scope.$on('$destroy', this.onDestroy.bind(this));

        this.$scope.$watch('eventExplanation', this.onEventExplanationChange.bind(this));
    }

    /**
     * Handles the event when invigilator authentication fails.
     * It logs the failure and updates the modal state to show an error message.
     */
    onInvigilatorFailed() {
        this.$scope.invigilatorUnlockCodeInputDisabled = false;
        this._unlockErrorMessage(this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_FAILED'));

        var eMsg = this.$translate.instant('MODULES.ADMIN.EVENTLOG_APPLICATION_FOCUS_LOST_INVIGILATOR_UNLOCK_FAILED');
        this.$eventLog.logEvent(eMsg, 'invLoginFailed', '', '', {});
        this.$scope.invigilatorUnlockCode = null;
    }

    /**
     * Handles the event when invigilator authentication succeeds.
     * It logs the success, updates the modal state, and checks if an explanation is required.
     * 
     * @param {string} data - The data received from the invigilator authentication.
     */
    onInvigilatorSuccess(data) {
        var result = JSON.parse(data);

        if (!angular.isObject(result) || result.type !== 'invigilator') {
            this.$logger.warn("Received invalid user data from invigilator login" + JSON.stringify(result));
            this.onInvigilatorFailed();
        } else if (result.type === 'invigilator') {
            this.$timeout(() => {
                this.$scope.$apply(() => {
                    this.$scope.invigilatorIsUnlocked = true;
                });
            }, 1);
            this.$signals.remove("tryInvigilatorAuthenticationFailed");
            this.$signals.remove("tryInvigilatorAuthenticationSuccess");

            var eMsg = this.$translate.instant('MODULES.ADMIN.EVENTLOG_APPLICATION_FOCUS_LOST_INVIGILATOR_UNLOCK_SUCCESS');
            this.$eventLog.logEvent(eMsg, 'invLoginSuccess', '', '', {});

            this.$scope.invigilatorUnlockCodeInputDisabled = false;

            var eventTypes = angular.copy(this.TaskInfoService.getTaskInfo().getEventTypes());
            var filteredEventType = this.$filter('filter')(eventTypes, { id: "focusLost" }, true)[0];
            if (angular.isDefined(filteredEventType) && filteredEventType.explanatoryNote === 'mandatory') {
                this.$scope.explanationMinChar = 1;
                this.$scope.disabledResumeBtn = true;
            }
        }
    }

    /**
     * Cleans up event listeners when the scope is destroyed.
     */
    onDestroy() {
        this.$signals.remove("tryInvigilatorAuthenticationFailed");
        this.$signals.remove("tryInvigilatorAuthenticationSuccess");
    }

    /**
     * Handles the unlock button click event.
     * It attempts to authenticate the invigilator with the provided unlock code.
     */
    onUnlockBtn() {
        this.$scope.errorMessage = null;
        if (this.$scope.invigilatorUnlockCode) {
            this.$scope.invigilatorUnlockCodeInputDisabled = true;
            this.$auth.tryInvigilatorAuthentication(this.$scope.invigilatorUnlockCode);
        } else {
            this._unlockErrorMessage(this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_EMPTY'));
        }
    }

    /**
     * Closes the unlock error message if it is currently displayed.
     */
    closeUnlockErrorMessage() {
        if (this.$scope.unlockHasError) {
            this._unlockErrorMessage(null);
        }
    }

    /**
     * Handles the keyup event on the password input field.
     * If the Enter key is pressed, it triggers the unlock button click event.
     * 
     * @param {Object} $event - The event object.
     */
    onKeyupPassword($event) {
        this.closeUnlockErrorMessage();
        if ($event.keyCode === 13) {
            this.onUnlockBtn();
        }
    }

    /**
     * Sets or clears the unlock error message.
     * If a message is provided, it sets the error message and schedules its removal after 5 seconds.
     * 
     * @param {string|null} message - The error message to display, or null to clear the message.
     * @returns {boolean} - Returns false if no message is provided.
     */
    _unlockErrorMessage(message) {
        if (!message) {
            this.$scope.unlockHasError = false;
            this.$scope.unlockErrorMessage = null;
            return false;
        }
        this.$scope.unlockHasError = true;
        this.$scope.unlockErrorMessage = message;

        if (this._removeUnlockHasErrorInterval) this.$interval.cancel(this._removeUnlockHasErrorInterval);
        this._removeUnlockHasErrorInterval = this.$interval(() => {
            this._unlockErrorMessage(null);
        }, 5000);
    }

    /**
     * Handles changes to the event explanation input field.
     * This method is currently a placeholder and does nothing.
     */
    onChangeExplanation() {
        // Existing code here
    }

    /**
     * Watches for changes to the event explanation and updates the state of the resume button accordingly.
     */
    onEventExplanationChange() {
        if (this.$scope.eventExplanation === false || this.$scope.explanationMinChar === 0) {
            this.$scope.disabledResumeBtn = false;
            return false;
        }

        if (this.$scope.eventExplanation && this.$scope.eventExplanation.length >= this.$scope.explanationMinChar) {
            this.$timeout(() => {
                this.$scope.$apply(() => {
                    this.$scope.disabledResumeBtn = false;
                });
            }, 1);
        } else {
            this.$scope.disabledResumeBtn = true;
        }
    }

    /**
     * Handles the close event of the modal.
     * Logs the focus lost duration and closes the modal if the resume button is not disabled.
     */
    onClose() {
        if (!this.$scope.disabledResumeBtn) {
            var endFocusLostTime = new Date().getTime(),
                focusLostMS = endFocusLostTime - this.startFocusLostTime,
                focusLostS = Math.floor(focusLostMS / 1000);
            this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.EVENTLOG_APPLICATION_FOCUS_LOST_DIALOG", { sec: focusLostS }), 'focusLostDialog', this.$scope.eventExplanation, '', { duration: focusLostMS });
            this.$uibModalInstance.close();
        }
    }

    /**
     * Handles the expiration of the modal.
     * Logs the focus lost duration and closes the modal.
     */
    expired() {
        var endFocusLostTime = new Date().getTime(),
            focusLostMS = endFocusLostTime - this.startFocusLostTime,
            focusLostS = Math.floor(focusLostMS / 1000);
        this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.EVENTLOG_APPLICATION_FOCUS_LOST_DIALOG_EXPIRED", { sec: focusLostS }), "focusLostDialogExpired", "", "", { duration: focusLostMS });
        this.$uibModalInstance.close();
    }
}

export default FocusRecoveredInvigilatorModalController;